import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import { Plus, Trash2 } from 'lucide-react';
import axios from 'axios';
import { logError } from '../../lib/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TEAMS = ['commanders', 'nationals', 'capitals', 'wizards', 'mystics', 'dcunited'];
const selectCls = 'text-xs border border-slate-300 px-2 py-1.5 bg-white';

export const TeamHubAdmin = () => {
  const [team, setTeam] = useState('commanders');
  const [polls, setPolls] = useState([]);
  const [storylines, setStorylines] = useState([]);
  const [settings, setSettings] = useState({});
  const [newPoll, setNewPoll] = useState({ question: '', options: '' });
  const [newStory, setNewStory] = useState({ title: '', note: '', article_id: '' });

  const load = useCallback(async () => {
    try {
      const [p, s, st] = await Promise.all([
        axios.get(`${API}/team-hub/polls`),
        axios.get(`${API}/team-hub/storylines/${team}`),
        axios.get(`${API}/team-hub/settings`),
      ]);
      setPolls(p.data.polls || []);
      setStorylines(s.data.storylines || []);
      setSettings(st.data.settings || {});
    } catch (error) {
      logError('team hub admin load failed:', error);
    }
  }, [team]);

  useEffect(() => { load(); }, [load]);

  const act = async (fn, okMsg) => {
    try {
      await fn();
      toast.success(okMsg);
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Action failed');
    }
  };

  const teamPolls = polls.filter((p) => p.team === team);

  return (
    <div data-testid="team-hub-admin" className="space-y-6">
      <div className="flex items-center gap-3">
        <label className="text-sm font-bold text-slate-700 uppercase">Team:</label>
        <select data-testid="hub-team-select" value={team} onChange={(e) => setTeam(e.target.value)} className={selectCls}>
          {TEAMS.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        {team === 'commanders' && (
          <label className="text-sm text-slate-600 flex items-center gap-2 ml-4">
            Featured module:
            <select
              data-testid="hub-featured-select"
              value={settings.commanders || 'roadto53'}
              onChange={(e) => act(() => axios.put(`${API}/team-hub/settings`, { team: 'commanders', featured_module: e.target.value }), 'Featured module updated')}
              className={selectCls}
            >
              <option value="roadto53">Road to 53 (featured on top)</option>
              <option value="season">Regular Season (Road to 53 archived lower)</option>
            </select>
          </label>
        )}
      </div>

      {/* Fan Polls */}
      <Card className="border border-slate-200">
        <CardHeader><CardTitle className="font-heading uppercase text-lg">Fan Polls — {team}</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Input data-testid="poll-question-input" value={newPoll.question} onChange={(e) => setNewPoll({ ...newPoll, question: e.target.value })} placeholder="Poll question" className="text-sm" />
            <Textarea data-testid="poll-options-input" value={newPoll.options} onChange={(e) => setNewPoll({ ...newPoll, options: e.target.value })} placeholder={'One option per line (2-5 options)'} rows={3} className="text-sm" />
            <Button
              size="sm" data-testid="poll-create-btn"
              disabled={!newPoll.question.trim() || newPoll.options.split('\n').filter((o) => o.trim()).length < 2}
              onClick={() => act(() => axios.post(`${API}/team-hub/polls`, {
                team, question: newPoll.question,
                options: newPoll.options.split('\n').map((o) => o.trim()).filter(Boolean),
                active: true,
              }).then(() => setNewPoll({ question: '', options: '' })), 'Poll created & activated')}
              className="rounded-none bg-[#002868]"
            >
              <Plus className="w-3 h-3 mr-1" /> Create & Activate
            </Button>
            <p className="text-[11px] text-slate-400">Creating a new active poll automatically deactivates the previous one for this team. Old results are preserved.</p>
          </div>
          <div className="divide-y divide-slate-100">
            {teamPolls.map((p) => (
              <div key={p.id} className="py-2">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-bold text-slate-900">
                    {p.question}
                    {p.active && <span className="ml-2 text-[10px] bg-green-700 text-white px-1.5 py-0.5 uppercase font-bold">Active</span>}
                  </p>
                  <Button
                    size="sm" variant="outline"
                    data-testid={`poll-toggle-${p.id}`}
                    onClick={() => act(() => axios.put(`${API}/team-hub/polls/${p.id}`, { active: !p.active }), p.active ? 'Poll deactivated' : 'Poll activated')}
                    className="h-7 rounded-none text-xs"
                  >
                    {p.active ? 'Deactivate' : 'Activate'}
                  </Button>
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  {p.total_votes} votes · {p.options.map((o) => `${o.text}: ${o.pct}%`).join(' · ')}
                </p>
              </div>
            ))}
            {teamPolls.length === 0 && <p className="text-xs text-slate-400 py-2">No polls yet for this team.</p>}
          </div>
        </CardContent>
      </Card>

      {/* Storylines */}
      <Card className="border border-slate-200">
        <CardHeader><CardTitle className="font-heading uppercase text-lg">What We're Watching — {team}</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap gap-2">
            <Input data-testid="story-title-input" value={newStory.title} onChange={(e) => setNewStory({ ...newStory, title: e.target.value })} placeholder="Storyline title" className="h-8 w-56 text-xs" />
            <Input value={newStory.note} onChange={(e) => setNewStory({ ...newStory, note: e.target.value })} placeholder="Short editorial note (optional)" className="h-8 flex-1 min-w-[180px] text-xs" />
            <Input value={newStory.article_id} onChange={(e) => setNewStory({ ...newStory, article_id: e.target.value })} placeholder="Linked article ID (optional)" className="h-8 w-56 text-xs" />
            <Button
              size="sm" data-testid="story-add-btn"
              disabled={!newStory.title.trim()}
              onClick={() => act(() => axios.post(`${API}/team-hub/storylines`, {
                team, title: newStory.title, note: newStory.note || null, article_id: newStory.article_id || null,
              }).then(() => setNewStory({ title: '', note: '', article_id: '' })), 'Storyline added')}
              className="rounded-none bg-[#5A1414] h-8"
            >
              <Plus className="w-3 h-3 mr-1" /> Add
            </Button>
          </div>
          <p className="text-[11px] text-slate-400">Newest 4 storylines show on the team page. Delete old ones to rotate.</p>
          <div className="divide-y divide-slate-100">
            {storylines.map((s) => (
              <div key={s.id} className="flex items-center justify-between py-1.5 text-sm">
                <span><b>{s.title}</b> {s.note && <span className="text-slate-500 text-xs">· {s.note}</span>}</span>
                <button onClick={() => act(() => axios.delete(`${API}/team-hub/storylines/${s.id}`), 'Deleted')} className="text-red-600">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
            {storylines.length === 0 && <p className="text-xs text-slate-400 py-2">No storylines yet for this team.</p>}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
